import type { Product } from "@/types";
import { validateSearchInput } from "./security";

/**
 * Search and filtering utilities for products
 */

/**
 * Safely convert any value to lowercase string
 */
function toSafeLower(val: unknown): string {
  return typeof val === "string" ? val.toLowerCase() : "";
}

/**
 * Filter products based on a search term
 * @param products - Array of products to search through
 * @param searchTerm - The search term to filter by
 * @returns Filtered array of products
 */
export function filterProductsBySearchTerm(
  products: Product[],
  searchTerm: string,
): Product[] {
  if (!Array.isArray(products) || products.length === 0) {
    return [];
  }

  if (!searchTerm || typeof searchTerm !== "string" || !searchTerm.trim()) {
    return products;
  }

  // Validate and sanitize the search term
  const validation = validateSearchInput(searchTerm);
  if (!validation.isValid) {
    return [];
  }

  const normalizedSearchTerm = toSafeLower(validation.sanitized);
  if (!normalizedSearchTerm) {
    return products;
  }

  return products.filter((product) => {
    if (!product || typeof product !== "object") return false;

    const productName = toSafeLower(product.name);
    const productBrand = toSafeLower(product.brand);
    const productComposition = toSafeLower(product.composition);

    const nameMatch = productName.includes(normalizedSearchTerm);
    const brandMatch = productBrand
      ? productBrand.includes(normalizedSearchTerm)
      : false;
    const compositionMatch = productComposition
      ? productComposition.includes(normalizedSearchTerm)
      : false;
    const categoryMatch = Array.isArray(product.categories)
      ? product.categories.some((cat) =>
          cat && typeof cat === "object"
            ? toSafeLower(cat.name).includes(normalizedSearchTerm)
            : false,
        )
      : false;

    return nameMatch || brandMatch || compositionMatch || categoryMatch;
  });
}

/**
 * Normalize search term by removing extra spaces and converting to lowercase
 * @param searchTerm - Raw search term
 * @returns Normalized search term
 */
export function normalizeSearchTerm(searchTerm: string): string {
  if (!searchTerm || typeof searchTerm !== "string") return "";
  return searchTerm.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Check if a search term is valid (non-empty after normalization and security validation)
 * @param searchTerm - Search term to validate
 * @returns Boolean indicating if the search term is valid
 */
export function isValidSearchTerm(searchTerm: string): boolean {
  if (!searchTerm || typeof searchTerm !== "string") return false;
  const validation = validateSearchInput(searchTerm);
  return (
    validation.isValid && normalizeSearchTerm(validation.sanitized).length > 0
  );
}

/**
 * Highlight search term matches in text
 * @param text - Text to highlight
 * @param searchTerm - Term to highlight
 * @returns Text with highlighted matches wrapped in <mark> tags
 */
export function highlightSearchTerm(text: string, searchTerm: string): string {
  if (!text || typeof text !== "string") {
    return "";
  }
  if (!isValidSearchTerm(searchTerm)) {
    return text;
  }

  const normalizedSearchTerm = normalizeSearchTerm(searchTerm);
  if (!normalizedSearchTerm) return text;
  const regex = new RegExp(`(${normalizedSearchTerm})`, "gi");

  return text.replace(regex, "<mark>$1</mark>");
}

/**
 * Extract search keywords from a search term
 * @param searchTerm - Search term to extract keywords from
 * @returns Array of individual keywords
 */
export function extractSearchKeywords(searchTerm: string): string[] {
  return normalizeSearchTerm(searchTerm)
    .split(" ")
    .filter((keyword) => keyword.length > 0);
}

/**
 * Score product relevance based on search term
 * @param product - Product to score
 * @param searchTerm - Search term to score against
 * @returns Relevance score (higher is more relevant)
 */
export function scoreProductRelevance(
  product: Product,
  searchTerm: string,
): number {
  if (!product || typeof product !== "object") {
    return 0;
  }
  if (!isValidSearchTerm(searchTerm)) {
    return 0;
  }

  const validation = validateSearchInput(searchTerm);
  if (!validation.isValid) {
    return 0;
  }

  const normalizedSearchTerm = normalizeSearchTerm(validation.sanitized);
  if (!normalizedSearchTerm) return 0;
  const keywords = extractSearchKeywords(validation.sanitized);

  let score = 0;

  const productName = toSafeLower(product.name);
  const productBrand = toSafeLower(product.brand);
  const productComposition = toSafeLower(product.composition);
  const categories = Array.isArray(product.categories)
    ? product.categories
    : [];

  // Exact name match gets highest score
  if (productName && productName === normalizedSearchTerm) {
    score += 100;
  }

  // Name starts with search term
  if (productName && productName.startsWith(normalizedSearchTerm)) {
    score += 80;
  }

  // Name contains search term
  if (productName && productName.includes(normalizedSearchTerm)) {
    score += 60;
  }

  // Brand exact match
  if (productBrand && productBrand === normalizedSearchTerm) {
    score += 50;
  }

  // Brand contains search term
  if (productBrand && productBrand.includes(normalizedSearchTerm)) {
    score += 30;
  }

  // Category exact match
  const exactCategoryMatch = categories.some((cat) => {
    if (!cat || typeof cat !== "object") return false;
    const catName = toSafeLower(cat.name);
    return catName === normalizedSearchTerm;
  });
  if (exactCategoryMatch) {
    score += 40;
  }

  // Category contains search term
  const categoriesMatch = categories.some((cat) => {
    if (!cat || typeof cat !== "object") return false;
    const catName = toSafeLower(cat.name);
    return catName.includes(normalizedSearchTerm);
  });
  if (categoriesMatch && !exactCategoryMatch) {
    score += 20;
  }

  // Composition contains search term
  if (productComposition && productComposition.includes(normalizedSearchTerm)) {
    score += 15;
  }

  // Keyword matches
  keywords.forEach((keyword) => {
    if (productName && productName.includes(keyword)) {
      score += 10;
    }
    if (productBrand && productBrand.includes(keyword)) {
      score += 5;
    }
    if (
      categories.some((cat) => {
        if (!cat || typeof cat !== "object") return false;
        return toSafeLower(cat.name).includes(keyword);
      })
    ) {
      score += 5;
    }
  });

  return score;
}

/**
 * Sort products by relevance to search term
 * @param products - Products to sort
 * @param searchTerm - Search term to sort by
 * @returns Products sorted by relevance (most relevant first)
 */
export function sortProductsByRelevance(
  products: Product[],
  searchTerm: string,
): Product[] {
  if (!Array.isArray(products)) {
    return [];
  }
  if (!isValidSearchTerm(searchTerm)) {
    return products;
  }

  return [...products].sort((a, b) => {
    const scoreA = scoreProductRelevance(a, searchTerm);
    const scoreB = scoreProductRelevance(b, searchTerm);
    return scoreB - scoreA;
  });
}
